import { createRouter, createWebHistory } from 'vue-router'
import { authGuard } from './guards'
import { useAuthStore } from '../stores/auth'
import { useFilesStore } from '../stores/files'
import HomeView from '../views/HomeView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      beforeEnter: (to, from, next) => {
        const authStore = useAuthStore()
        if (authStore.isAuthenticated) {
          next('/app')
        } else {
          next()
        }
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: { guestOnly: true }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/ForgotPasswordView.vue'),
      meta: { guestOnly: true }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/ResetPasswordView.vue'),
      meta: { guestOnly: true }
    },
    {
      path: '/app',
      component: () => import('../layouts/MainLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          redirect: '/app/dashboard'
        },
        {
          path: 'dashboard',
          name: 'dashboard-view',
          alias: '/dashboard',  // Allow /dashboard shorthand
          beforeEnter: (to, from, next) => {
            const authStore = useAuthStore()
            if (authStore.user?.role === 'user') {
              // Redirect regular users to Files with month navigation
              next({ name: 'files', query: { navigateToMonth: 'true' } })
            } else {
              next()
            }
          },
          component: () => {
            // Dynamically load the appropriate dashboard based on user role
            const authStore = useAuthStore()
            if (authStore.user?.role === 'accountant') {
              return import('../views/AccountantDashboard.vue')
            }
            // Admin users will see the regular dashboard
            return import('../views/DashboardView.vue')
          }
        },
        {
          path: 'chat',
          name: 'chat',
          alias: '/chat',  // Allow /chat shorthand
          component: () => import('../views/ChatView.vue')
        },
        {
          path: 'chat/:conversationId',
          name: 'conversation',
          alias: '/chat/:conversationId',  // Allow /chat/:id shorthand
          component: () => import('../views/ChatView.vue')
        },
        {
          // Legacy: chat was previously routed under /conversations.
          // Redirect to /chat, forwarding the conversation id, query, and hash.
          path: 'conversations/:conversationId',
          alias: '/conversations/:conversationId',
          redirect: (to) => ({
            name: 'conversation',
            params: { conversationId: to.params.conversationId },
            query: to.query,
            hash: to.hash
          })
        },
        {
          // Legacy: /conversations -> /chat (forward query + hash)
          path: 'conversations',
          alias: '/conversations',
          redirect: (to) => ({ name: 'chat', query: to.query, hash: to.hash })
        },
        {
          path: 'files',
          name: 'files',
          alias: '/files',  // Allow /files to work as shorthand
          component: () => import('../views/FilesView.vue'),
          beforeEnter: (to, from, next) => {
            const authStore = useAuthStore()
            const filesStore = useFilesStore()
            
            // For admin/accountant without clientId, check if there's one in the store
            if (
              (authStore.user?.role === 'admin' || authStore.user?.role === 'accountant') &&
              !to.query.clientId
            ) {
              // If coming from a folder view, preserve the client context
              const storedClientId = filesStore.getSelectedClientId()
              if (storedClientId && from.name === 'folder') {
                // Redirect to files with preserved clientId
                next({ name: 'files', query: { clientId: storedClientId } })
              } else {
                // No client context, redirect to selection
                next({ name: 'files-select' })
              }
            } else {
              // Add flag to indicate month-based navigation if coming from nav click
              if (to.query.navigateToMonth === 'true') {
                next()
              } else {
                next()
              }
            }
          }
        },
        {
          path: 'files-select',
          name: 'files-select',
          component: () => import('../views/FilesUserListView.vue'),
          meta: { requiresAnyRole: ['admin', 'accountant'] }
        },
        {
          path: 'anaf',
          name: 'anaf',
          alias: '/anaf',
          component: () => import('../views/AnafConnectorView.vue'),
          meta: { requiresAnyRole: ['admin', 'accountant'] }
        },
        {
          path: 'files/:folderId',
          name: 'folder',
          component: () => import('../views/FilesView.vue')
        },
        {
          path: 'statistics',
          name: 'statistics',
          alias: '/statistics',  // Allow /statistics shorthand
          component: () => import('../views/StatisticsView.vue')
        },
        {
          path: 'invoices',
          name: 'invoices',
          alias: '/invoices',
          component: () => import('../views/InvoicesView.vue'),
          meta: { requiresInvoicing: true }
        },
        {
          path: 'invoices/new',
          name: 'invoice-new',
          component: () => import('../views/InvoiceCreateView.vue'),
          meta: { requiresInvoiceIssuance: true }
        },
        {
          path: 'invoices/:id',
          name: 'invoice-detail',
          component: () => import('../views/InvoiceDetailView.vue'),
          meta: { requiresInvoicing: true }
        },
        {
          // Firm-scoped monthly report for accountants (admins reach the same
          // component through their own Subscriptions page). Hidden entirely
          // while enforcement is off — there is nothing to report on yet.
          path: 'subscriptions/report',
          name: 'subscriptions-report',
          component: () => import('../views/SubscriptionsReportView.vue'),
          meta: {
            requiresAnyRole: ['admin', 'accountant'],
            requiresSubscriptionsEnforced: true
          }
        },
        {
          path: 'notifications',
          name: 'notifications',
          alias: '/notifications',  // Allow /notifications shorthand
          component: () => import('../views/NotificationsView.vue')
        },
        {
          path: 'settings',
          name: 'settings',
          alias: '/settings',  // Allow /settings shorthand
          component: () => import('../views/SettingsView.vue')
        },
        {
          path: 'feedback',
          name: 'feedback',
          alias: '/feedback',  // Allow /feedback shorthand
          component: () => import('../views/FeedbackView.vue')
        },
        {
          path: 'clients/:id',
          name: 'client-edit',
          component: () => import('../views/admin/ClientEditView.vue'),
          meta: { requiresAnyRole: ['admin', 'accountant'] }
        },
        {
          path: 'admin',
          meta: { requiresRole: 'admin' },
          children: [
            {
              path: 'users',
              name: 'admin-users',
              component: () => import('../views/admin/UsersView.vue')
            },
            {
              path: 'users/:id',
              name: 'admin-user-edit',
              component: () => import('../views/admin/UserEditView.vue')
            },
            {
              path: 'firms',
              name: 'admin-firms',
              component: () => import('../views/admin/FirmsView.vue')
            },
            {
              path: 'firms/new',
              name: 'admin-firm-create',
              component: () => import('../views/admin/FirmEditView.vue')
            },
            {
              path: 'firms/:id',
              name: 'admin-firm-edit',
              component: () => import('../views/admin/FirmEditView.vue')
            },
            {
              path: 'clients',
              name: 'admin-clients',
              component: () => import('../views/admin/ClientsView.vue')
            },
            {
              path: 'clients/:id',
              name: 'admin-client-edit',
              component: () => import('../views/admin/ClientEditView.vue')
            },
            {
              path: 'audit',
              name: 'admin-audit',
              component: () => import('../views/admin/AuditView.vue')
            },
            {
              // `?client=<id>` deep-links straight into that client's dialog.
              path: 'subscriptions',
              name: 'admin-subscriptions',
              component: () => import('../views/admin/SubscriptionsAdminView.vue')
            }
          ]
        }
      ]
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: () => import('../views/UnauthorizedView.vue')
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue')
    }
  ]
})

router.beforeEach(authGuard)

export default router
